/* eslint-disable @typescript-eslint/no-floating-promises */
import "expo-dev-client"
import { LogBox, Platform, UIManager, View } from "react-native"
import Head from "expo-router/head"
import {
  ErrorBoundary as ExpoErrorBoundary,
  type ErrorBoundaryProps,
  SplashScreen,
  Stack,
  useGlobalSearchParams,
  useNavigationContainerRef,
} from "expo-router"
import * as Linking from "expo-linking"
import initStore from "@/stores/store"
import { useStore } from "@nanostores/react"
import { DefaultTheme, ThemeProvider } from "@react-navigation/native"
import { useFonts } from "expo-font"
import { SafeAreaProvider } from "react-native-safe-area-context"
import AppToast from "@/components/toasts/AppToast"

import { useReactNavigationDevTools } from "@dev-plugins/react-navigation"
import { useReactQueryDevTools } from "@dev-plugins/react-query"

import { useEffect, useLayoutEffect, useState } from "react"

import { queryClient } from "@/queries/client"

import {
  setupOnlineManager,
  useQueryAppStateChangeSetup,
} from "@/queries/client/managers"
import { QueryClientProvider } from "@tanstack/react-query"
import { segment } from "@/analytics/client"
import { AppAnalyticsProvider, useRootScreenTracking } from "@/analytics/react"
import { $appIsLoading } from "@/stores/appStore"

import BackflipSplashScreen from "@/app/splash"
import "@/assets/css/global.css"
import useAppInitializer from "@/hooks/appInitializer"
import { $authToken, setAuthToken } from "@/stores/auth"

import { client } from "@/api/sdk"
import { Sentry, useSentry } from "@/hooks/sentry"
import { useLatestBranchOpenEvent } from "@/stores/branch"
import { useInitOneSignal } from "@/stores/oneSignal"
import { setRefCode } from "@/stores/refCode"
import { StatusBar } from "expo-status-bar"
import { $statusBarStyle } from "@/stores/statusBarStyle"
import { reloadAsync } from "expo-updates"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { resetUtm, updateUtm } from "@/stores/utm"
import { userCapturedByAd } from "@/analytics/events/register"
import BFText from "@/components/standard/BFText"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import { useTagManager } from "@/hooks/tagManager"
import { AutocompleteDropdownContextProvider } from "react-native-autocomplete-dropdown"
import { useIncomingShareIntent } from "@/hooks/useIncomingShareIntent"
import { setFullStoryUser } from "@/hooks/fullStory"
import { LDProviderWrapper } from "@/services/components/LDProviderWrapper"

import "@/dev.menu"
import { PortalHost } from "@rn-primitives/portal"
import { AttributionPopup } from "@/components/user/AttributionPopup"
import { ForceUpgradeGate } from "@/components/modals/ForceUpgradeGate"
import { useUser } from "@/queries/user"
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import { GestureHandlerRootView } from "react-native-gesture-handler"

LogBox.ignoreLogs([
  "Linking found multiple possible URI schemes in your Expo config.",
  "Invalid prop `dataElement` supplied to `React.Fragment`. React.Fragment can only have `key` and `children` props.",
  "Invalid prop `dataElement` supplied to `React.Fragment`", // FullStory automatic attributes
])

if (Platform.OS === "web") {
  // Check console on web for errors.
  LogBox.ignoreAllLogs(true)
}

if (Platform.OS === "android") {
  if (UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true)
  }
}

type UrlSearchParams = {
  utm_source?: string | string[]
  utm_medium?: string | string[]
  utm_campaign?: string | string[]
  utm_term?: string | string[]
  utm_content?: string | string[]
  token?: string | string[]
} & Record<string, string | string[]>

export function ErrorBoundary(props: ErrorBoundaryProps) {
  const testBoundary = false
  if (__DEV__ && !testBoundary) {
    return <ExpoErrorBoundary {...props} />
  }

  useEffect(() => {
    Sentry.captureMessage(`ErrorBoundary: Error caught ${props.error.message}`)
    if (props.error) {
      Sentry.captureException(props.error)
    }
  }, [props.error])

  return (
    <View className="flex flex-col items-center justify-center flex-1 bg-midnight-blue">
      <View className="flex flex-col gap-8">
        <BFText className="font-figtree-extrabold text-[24px] text-white">
          Something went wrong.
        </BFText>
        <PrimaryButton
          testID={"retry-button"}
          title="Try Again?"
          onPress={() => {
            void reloadAsync()
          }}
        />
      </View>
    </View>
  )
}

// eslint-disable-next-line camelcase, @typescript-eslint/naming-convention
export const unstable_settings = {
  // Ensure that reloading on `/modal` keeps a back button present.
  initialRouteName: "(tabs)",
}

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync()

// Init store
initStore()

setupOnlineManager()

function RootLayout() {
  const {
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_content: utmContent,
    token,
  } = useGlobalSearchParams<UrlSearchParams>()
  const url = Linking.useURL()
  const event = useLatestBranchOpenEvent()
  const appIsLoading = useStore($appIsLoading)
  const authToken = useStore($authToken)
  const navigationRef = useNavigationContainerRef()
  const { isWeb } = useDeviceAttributes()

  useReactNavigationDevTools(navigationRef)
  useReactQueryDevTools(queryClient)

  useIncomingShareIntent()
  useTagManager()
  useSentry()
  useAppInitializer()
  useRootScreenTracking()

  useInitOneSignal()

  useQueryAppStateChangeSetup()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loaded] = useFonts({
    "Figtree-Bold": require("../assets/fonts/Figtree-Bold.ttf"),
    "Figtree-ExtraBold": require("../assets/fonts/Figtree-ExtraBold.ttf"),
    "SpaceGrotesk-Regular": require("../assets/fonts/SpaceGrotesk-Regular.ttf"),
    "SpaceGrotesk-Bold": require("../assets/fonts/SpaceGrotesk-Bold.ttf"),
  })

  useLayoutEffect(() => {
    const token = authToken

    if (token) {
      client.setHeader("authorization", `jwt ${token}`)
    } else {
      client.setHeader("authorization", "")
      const userInfo = segment?.userInfo?.get()
      if (userInfo) {
        setFullStoryUser(userInfo.anonymousId)
      }
    }
  }, [authToken])

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync()
    }
  }, [loaded])

  useEffect(() => {
    if (url) {
      const { hostname, path, queryParams } = Linking.parse(url)
      console.log(
        `Linked to app with hostname: ${hostname}, path: ${path} and data: ${JSON.stringify(
          queryParams
        )}`
      )
      let refCode = queryParams?.code
      // don't mistake the google auth redirect code for a ref code
      if (refCode && !path?.includes("google")) {
        refCode = Array.isArray(refCode) ? refCode[0] : refCode
        console.log(`refCode: ${refCode}`)
        setRefCode(refCode)
      }
    }
  }, [url])

  useEffect(() => {
    if (event) {
      const refCode = event.params?.code
      const utmMedium = event.params?.["~feature"]
      const utmSource = event.params?.["~channel"]
      const utmCampaign = event.params?.["~campaign"]
      const utmContent = event.params?.utm_content as string
      const utmTerm = event.params?.utm_term as string

      if (refCode) {
        console.log(`branch refCode: ${refCode as string}`)
        setRefCode(refCode as string)
      }

      if (utmMedium || utmSource || utmCampaign || utmContent || utmTerm) {
        console.log(
          `branch utm: ${utmMedium} ${utmSource} ${utmCampaign} ${utmContent} ${utmTerm}`
        )
        resetUtm()
        updateUtm({
          utmMedium,
          utmSource,
          utmCampaign,
          utmContent,
          utmTerm,
        })
        userCapturedByAd({
          utmMedium,
          utmSource,
          utmCampaign,
          utmContent,
          utmTerm,
        })
      }
    }
  }, [event])

  useEffect(() => {
    if (utmCampaign || utmContent || utmMedium || utmSource || utmTerm) {
      function stringValue(
        value?: string | string[] | undefined
      ): string | undefined {
        if (Array.isArray(value)) {
          console.warn("Unexpected array value for UTM", value)
          return value[0]
        }
        return value
      }

      const UTMs = {
        utmCampaign: stringValue(utmCampaign),
        utmContent: stringValue(utmContent),
        utmMedium: stringValue(utmMedium),
        utmSource: stringValue(utmSource),
        utmTerm: stringValue(utmTerm),
      }

      resetUtm()
      updateUtm(UTMs)
      userCapturedByAd(UTMs)
    }
  }, [utmCampaign, utmContent, utmMedium, utmSource, utmTerm])

  useEffect(() => {
    if (token) {
      const tokenValue = Array.isArray(token) ? token[0] : token
      console.log("Setting auth token from URL parameter")
      setAuthToken(tokenValue)
    }
  }, [token])

  if (appIsLoading && !isWeb) {
    return <BackflipSplashScreen />
  }

  return <RootLayoutNav />
}

const RootLayoutStatusBar = () => {
  const statusBarStyle = useStore($statusBarStyle)
  return <StatusBar style={statusBarStyle.override || statusBarStyle.base} />
}

const GlobalAttributionPopup = () => {
  const { data: user, isLoading, isError } = useUser()
  const [showAttributionPopup, setShowAttributionPopup] = useState(false)

  useEffect(() => {
    if (!isLoading && !isError && user) {
      const userAttribution = user?.me?.attribution
      setShowAttributionPopup(userAttribution === null)
    }
  }, [isLoading, isError, user])

  return (
    <AttributionPopup
      visible={showAttributionPopup}
      onClose={() => {
        setShowAttributionPopup(false)
      }}
    />
  )
}

const RootLayoutNav = () => {
  return (
    <AppAnalyticsProvider>
      <AutocompleteDropdownContextProvider>
        <QueryClientProvider client={queryClient}>
          <LDProviderWrapper>
            <SafeAreaProvider>
              <GestureHandlerRootView style={{ flex: 1 }}>
                <ThemeProvider value={DefaultTheme}>
                  <BottomSheetModalProvider>
                    <Head>
                      <title>Backflip</title>
                    </Head>

                    <Stack screenOptions={{ headerShown: false }}>
                      <Stack.Screen
                        name="(tabs)"
                        options={{ headerShown: false }}
                      />
                      <Stack.Screen
                        name="[...missing]"
                        options={{
                          headerShown: false,
                        }}
                      />
                    </Stack>
                    <RootLayoutStatusBar />
                    <AppToast />
                    <GlobalAttributionPopup />
                    <PortalHost />
                    <ForceUpgradeGate />
                  </BottomSheetModalProvider>
                </ThemeProvider>
              </GestureHandlerRootView>
            </SafeAreaProvider>
          </LDProviderWrapper>
        </QueryClientProvider>
      </AutocompleteDropdownContextProvider>
    </AppAnalyticsProvider>
  )
}

export default Sentry.wrap(RootLayout)
